import React from "react";
import { Button, Typography, Box, Alert } from "@mui/material";
import { Warning as WarningIcon } from "@mui/icons-material";
import BaseModal from "./BaseModal";

export interface ResetWarningModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentStep: string;
  targetStep: string;
  stepsToReset: string[];
  title?: string;
  message?: string;
  confirmButtonText?: string; // Custom button text for confirm action
}

const ResetWarningModal = ({
  open,
  onClose,
  onConfirm,
  currentStep,
  targetStep,
  stepsToReset,
  title = "Reset Analysis Progress",
  message,
  confirmButtonText = "Reset and Go Back",
}: ResetWarningModalProps) => {
  const getStepDisplayName = (stepId: string) => {
    switch (stepId) {
      case "quality-control":
        return "Step 1: Quality Control";
      case "filter-cells-genes":
        return "Step 2: Filter Cells & Genes";
      case "doublet-detection":
        return "Step 3: Doublet Detection (Optional)";
      case "normalization":
        return "Step 4: Normalization";
      case "feature-selection":
        return "Step 5: Feature Selection";
      case "dimensionality-reduction":
        return "Step 6: Dimensionality Reduction";
      case "umap":
        return "Step 7: UMAP";
      case "clustering":
        return "Step 8: Clustering";
      case "cell-type-annotation":
        return "Step 9: Cell Type Annotation";
      case "marker-gene-set":
        return "Step 9: Cell Type Identification";
      case "de-genes-markers":
        return "Step 11: Cell Type Markers & Biomarker Discovery";
      case "enrichment":
        return "Step 12: Enrichment";
      case "trajectory-analysis":
        return "Step 13: Trajectory Analysis";
      default:
        return stepId;
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon sx={{ color: "#f59e0b", fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2937" }}>
            {title}
          </Typography>
        </Box>
      }
      maxWidth="sm"
    >
      <Box sx={{ pt: 2 }}>
        <Alert severity="warning" sx={{ mb: 3 }} icon={false}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {message ||
              (currentStep === targetStep ? (
                <>
                  Going back to the form view will reset all progress made after
                  this step.
                </>
              ) : (
                <>
                  Going back to {getStepDisplayName(targetStep)} will reset all
                  progress made after this step.
                </>
              ))}
          </Typography>
        </Alert>

        <Typography variant="body1" sx={{ mb: 2, color: "#374151" }}>
          {currentStep === "all" ? (
            <>
              You want to reset all analysis progress and return to{" "}
              <strong>{getStepDisplayName(targetStep)}</strong>.
            </>
          ) : currentStep === targetStep ? (
            <>
              You are currently viewing the results of{" "}
              <strong>{getStepDisplayName(currentStep)}</strong> and want to go
              back to its form view.
            </>
          ) : (
            <>
              You are currently at{" "}
              <strong>{getStepDisplayName(currentStep)}</strong> and want to go
              back to <strong>{getStepDisplayName(targetStep)}</strong>.
            </>
          )}
        </Typography>

        {stepsToReset.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "#6b7280", mb: 1 }}
            >
              The following steps will be reset and need to be run again:
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              {stepsToReset.map((stepId) => (
                <Typography
                  key={stepId}
                  component="li"
                  variant="body2"
                  sx={{ color: "#6b7280", mb: 0.5 }}
                >
                  {getStepDisplayName(stepId)}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        <Typography variant="body2" sx={{ mt: 2, color: "#6b7280" }}>
          Are you sure you want to continue? This action cannot be undone.
        </Typography>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 4,
            pt: 2,
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            color="primary"
            sx={{
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              py: 1,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              py: 1,
              backgroundColor: "#dc2626",
              "&:hover": {
                backgroundColor: "#b91c1c",
              },
            }}
          >
            {confirmButtonText}
          </Button>
        </Box>
      </Box>
    </BaseModal>
  );
};

export default ResetWarningModal;
